import { useCallback, useEffect, useMemo, useState } from "react";
import { PreviewButton } from "@/components/PdfPreview";
import { useNavigate } from "react-router-dom";
import { FileDown, History, FileSpreadsheet } from "lucide-react";
import api, { download } from "@/lib/api";
import { money, pct, fdate, searchOpts, matchKw } from "@/lib/fmt";
import { PageHeader, StatusBadge, VersionBadge, Validity, Empty } from "@/components/Common";
import { Autocomplete } from "@/components/Autocomplete";
import { ImportDialog } from "@/components/ImportDialog";
import { HistoryDialog } from "@/components/HistoryDialog";
import { SalesFilter } from "@/components/SalesFilter";
import { Button } from "@/components/ui/button";
import { QuoteRowActions } from "@/components/QuoteRowActions";
import { probTone } from "@/components/ProbabilityEditor";

const KEYS = ["display_no", "customer_name", "project_title", "sales_name", "pic_name"];

export default function Quotations() {
  const [rows, setRows] = useState([]);
  const [kw, setKw] = useState("");
  const [status, setStatus] = useState("");
  const [sales, setSales] = useState("");
  const [hist, setHist] = useState(null);
  const nav = useNavigate();
  const load = useCallback(() => api.get("/quotations", { params: { sales_id: sales } }).then((r) => setRows(r.data)), [sales]);
  useEffect(() => { load(); }, [load]);
  const list = useMemo(() => rows.filter((r) => matchKw(r, kw, KEYS) && (!status || r.display_status === status)), [rows, kw, status]);
  const params = { q: kw, status, sales_id: sales };

  return (
    <div>
      <PageHeader title="Penawaran" subtitle="Daftar penawaran versi terakhir. Klik untuk detail, revisi, nego & cetak PDF.">
        <SalesFilter value={sales} onChange={setSales} />
        <ImportDialog entity="quotations" title="Penawaran" onDone={load} />
        <PreviewButton url="/reports/quotations" params={{ ...params, fmt: "pdf" }} filename="Laporan-Penawaran.pdf" title="Laporan Penawaran" testId="quotations-export-preview-btn" />
        <Button variant="outline" className="bg-white" onClick={() => download("/reports/quotations", { ...params, fmt: "pdf" }, "Laporan-Penawaran.pdf")} data-testid="quotations-export-pdf-btn"><FileDown className="h-4 w-4 mr-1" />PDF</Button>
        <Button variant="outline" className="bg-white" onClick={() => download("/reports/quotations", { ...params, fmt: "xlsx" }, "Laporan-Penawaran.xlsx")} data-testid="quotations-export-excel-btn"><FileSpreadsheet className="h-4 w-4 mr-1" />Excel</Button>
      </PageHeader>
      <div className="card-premium fade-up">
        <div className="flex flex-col gap-3 p-5 md:flex-row">
          <Autocomplete value={kw} onChange={setKw} options={searchOpts(rows, KEYS)} icon placeholder="Cari no, customer, project, sales..." testId="quotations-search" className="md:w-96" />
          <select className="h-10 rounded-md border border-input bg-white px-3 text-sm" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="quotations-status-filter">
            <option value="">Semua Status</option><option value="open">Aktif</option><option value="expired">Expired</option><option value="deal">Deal</option><option value="lost">Lost</option>
          </select>
        </div>
        <div className="overflow-x-auto">
          <table className="tbl w-full" data-testid="quotations-table">
            <thead><tr><th>No Penawaran</th><th>Tanggal</th><th>Berlaku</th><th>Customer</th><th>Sales</th><th className="text-right">Total</th><th className="text-right">Margin</th><th className="text-right">Possibility</th><th>Versi</th><th>Status</th><th /></tr></thead>
            <tbody>
              {list.map((q, i) => (
                <tr key={q.id} className="cursor-pointer" onClick={() => nav(`/quotations/${q.id}`)} data-testid={`quotation-row-${i}`}>
                  <td className="font-num text-sm font-semibold text-blue-800">{q.display_no}</td>
                  <td className="text-sm">{fdate(q.created_at)}</td>
                  <td><Validity until={q.valid_until} status={q.display_status} /></td>
                  <td><p className="font-medium">{q.customer_name}</p><p className="text-xs text-slate-400">{q.project_title}</p></td>
                  <td className="text-sm">{q.sales_name}</td>
                  <td className="text-right font-num font-semibold">{money(q.grand_total ?? q.offer_total, q.currency)}{q.ppn_enabled && <div className="text-[10px] font-normal text-slate-400">incl. PPN {q.ppn_percent}%</div>}</td>
                  <td className="text-right font-num">{pct(q.final_margin)}</td>
                  <td className={`text-right font-num font-semibold ${probTone(q.probability)}`} data-testid={`quotation-prob-${i}`}>{q.probability == null ? "-" : `${q.probability}%`}</td>
                  <td><VersionBadge q={q} /></td>
                  <td><StatusBadge status={q.display_status} /></td>
                  <td onClick={(e) => e.stopPropagation()} className="whitespace-nowrap">
                    <Button size="icon" variant="ghost" title="PDF" onClick={() => download(`/quotations/${q.id}/pdf`, {}, q.pdf_filename)} data-testid={`quotation-pdf-${i}`}><FileDown className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" title="Riwayat" onClick={() => setHist(q.id)} data-testid={`quotation-history-${i}`}><History className="h-4 w-4" /></Button>
                    <QuoteRowActions id={q.id} displayNo={q.display_no} fileName={q.pdf_filename} onDeleted={load} testPrefix={`quotation-${i}`} locked={["deal", "lost"].includes(q.status)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!list.length && <Empty />}
        </div>
      </div>
      <HistoryDialog quoteId={hist} open={!!hist} onOpenChange={(o) => !o && setHist(null)} />
    </div>
  );
}
